import { dataBackend, supabase, supabaseConfigured } from './supabaseClient';
import { MAX_IMAGE_BYTES, matchesImageSignature } from './imageUpload';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
const publicTables = { news: 'articles', services: 'services', results: 'results', testimonials: 'testimonials' };

function response(data, status = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json; charset=utf-8', 'Cache-Control': 'no-store' },
  });
}

function errorResponse(message, status = 500, code = 'request_error') {
  return response({ detail: message, error: { code, message } }, status);
}

function requestPath(input) {
  const raw = typeof input === 'string' ? input : input.url;
  try {
    return new URL(raw, window.location.origin).pathname + new URL(raw, window.location.origin).search;
  } catch {
    return raw;
  }
}

function apiPath(input) {
  const value = requestPath(input);
  const position = value.indexOf('/api/');
  return position >= 0 ? value.slice(position) : value;
}

function parseBody(init = {}) {
  if (!init.body || init.body instanceof FormData) return init.body;
  if (typeof init.body === 'string') {
    try { return JSON.parse(init.body); } catch { return {}; }
  }
  return init.body;
}

function articleShape(row) {
  return { ...row, id: row.legacy_id || row.id, imageUrl: row.image_url };
}

function orderedShape(row) {
  return { ...row, id: row.legacy_id || row.id, order: row.display_order };
}

function rowShape(table, row) {
  return table === 'articles' ? articleShape(row) : orderedShape(row);
}

function slugify(value = '') {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 180) || crypto.randomUUID();
}

export function toDatabaseRow(table, value, isCreate = false) {
  const row = { ...value };
  delete row.id;
  delete row.legacy_id;
  if ('imageUrl' in row) {
    row.image_url = row.imageUrl || null;
    delete row.imageUrl;
  }
  if ('order' in row) {
    row.display_order = Number(row.order) || 0;
    delete row.order;
  }
  if (table === 'articles' && isCreate) {
    row.slug = row.slug || slugify(row.title);
  }
  // All content starts as a draft; publication always requires an explicit action.
  if (isCreate && row.published === undefined) row.published = false;
  return row;
}

async function authHeaders(headers = {}) {
  const next = new Headers(headers);
  const { data } = await supabase.auth.getSession();
  if (data.session?.access_token) next.set('Authorization', `Bearer ${data.session.access_token}`);
  next.set('apikey', process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY);
  return next;
}

async function edge(name, init = {}, query = '') {
  const headers = await authHeaders(init.headers);
  if (!(init.body instanceof FormData) && init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const result = await window.fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}${query}`, { ...init, headers });
  const payload = await result.json().catch(() => null);
  if (!payload || typeof payload.ok !== 'boolean') return errorResponse('Risposta server non valida', 502, 'invalid_response');
  return payload.ok
    ? response(payload.data, result.status)
    : errorResponse(payload.error?.message || 'Richiesta non riuscita', result.status, payload.error?.code);
}

async function publicRead(path) {
  const url = new URL(path, window.location.origin);
  const pieces = url.pathname.replace(/^\/api\//, '').split('/').filter(Boolean);
  const resource = pieces[0];
  const table = publicTables[resource];
  if (!table) return null;
  const includeDrafts = url.searchParams.get('published_only') === 'false';
  let query = supabase.from(table).select('*');
  // The public UI stays public even when visited with the administrator's session.
  // RLS remains authoritative when an unauthorised caller requests includeDrafts.
  if (!includeDrafts) query = query.eq('published', true);
  if (resource === 'news') {
    if (pieces[1] === 'featured') query = query.eq('featured', true).limit(10);
    else if (pieces[1]) {
      const value = pieces[1];
      query = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value)
        ? query.or(`id.eq.${value},legacy_id.eq.${value}`).maybeSingle()
        : query.eq('legacy_id', value).maybeSingle();
      const { data, error } = await query;
      if (error) return errorResponse(error.message, 500, 'database_error');
      if (!includeDrafts && data?.scheduled_at && new Date(data.scheduled_at).getTime() > Date.now()) {
        return errorResponse('Articolo non trovato', 404, 'not_found');
      }
      return data ? response(articleShape(data)) : errorResponse('Articolo non trovato', 404, 'not_found');
    }
    if (!includeDrafts) query = query.or(`scheduled_at.is.null,scheduled_at.lte.${new Date().toISOString()}`);
    query = query.order('created_at', { ascending: false });
  } else {
    query = query.order(resource === 'testimonials' ? 'created_at' : 'display_order', { ascending: resource !== 'testimonials' });
  }
  const { data, error } = await query;
  if (error) return errorResponse(error.message, 500, 'database_error');
  return response((data || []).map((row) => rowShape(table, row)));
}

async function adminContent(path, init) {
  const url = new URL(path, window.location.origin);
  const match = url.pathname.match(/^\/api\/admin\/(news|services|results|testimonials)(?:\/([^/]+))?(?:\/(toggle|featured|schedule))?$/);
  if (!match) return null;
  const [, resource, externalId, action] = match;
  const table = publicTables[resource];
  const method = (init.method || 'GET').toUpperCase();
  const body = parseBody(init) || {};
  const resolveId = async () => {
    if (!externalId) return null;
    const base = supabase.from(table).select('id');
    const lookup = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(externalId)
      ? base.or(`id.eq.${externalId},legacy_id.eq.${externalId}`)
      : base.eq('legacy_id', externalId);
    const { data } = await lookup.maybeSingle();
    return data?.id || null;
  };
  if (method === 'DELETE') {
    const id = await resolveId();
    if (!id) return errorResponse('Elemento non trovato', 404, 'not_found');
    const { error } = await supabase.from(table).delete().eq('id', id);
    return error ? errorResponse(error.message, 400, 'database_error') : response({ message: 'Elemento eliminato' });
  }
  if (method === 'PATCH' && externalId) {
    const id = await resolveId();
    if (!id) return errorResponse('Elemento non trovato', 404, 'not_found');
    let update = {};
    if (action === 'toggle') {
      const { data } = await supabase.from(table).select('published').eq('id', id).single();
      update = { published: !data?.published };
    } else if (action === 'featured' && table === 'articles') {
      const { data } = await supabase.from(table).select('featured').eq('id', id).single();
      update = { featured: !data?.featured };
    } else if (action === 'schedule' && table === 'articles') {
      update = { scheduled_at: body.scheduled_at || null, published: true };
    } else {
      update = toDatabaseRow(table, body);
    }
    const { data, error } = await supabase.from(table).update(update).eq('id', id).select('*').single();
    return error ? errorResponse(error.message, 400, 'database_error') : response(rowShape(table, data));
  }
  if (method === 'PUT' && externalId) {
    const id = await resolveId();
    const { data, error } = await supabase.from(table).update(toDatabaseRow(table, body)).eq('id', id).select('*').single();
    return error ? errorResponse(error.message, 400, 'database_error') : response(rowShape(table, data));
  }
  if (method === 'POST' && !externalId) {
    const { data, error } = await supabase.from(table).insert(toDatabaseRow(table, body, true)).select('*').single();
    return error ? errorResponse(error.message, 400, 'database_error') : response(rowShape(table, data), 201);
  }
  return null;
}

async function uploadImage(init) {
  const file = init.body instanceof FormData ? init.body.get('file') : null;
  if (!(file instanceof File)) return errorResponse('File mancante', 422, 'validation_error');
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size === 0 || file.size > MAX_IMAGE_BYTES) {
    return errorResponse('Formato o dimensione file non validi', 422, 'validation_error');
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (!matchesImageSignature(bytes, file.type)) {
    return errorResponse('Il contenuto del file non corrisponde al formato dichiarato', 422, 'validation_error');
  }
  const digest = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map((value) => value.toString(16).padStart(2, '0')).join('');
  const extension = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' }[file.type];
  const objectPath = `uploaded/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${extension}`;
  const { error: uploadError } = await supabase.storage.from('article-media').upload(objectPath, file, { contentType: file.type, upsert: false });
  if (uploadError) return errorResponse(uploadError.message, 400, 'storage_error');
  const { data: publicUrl } = supabase.storage.from('article-media').getPublicUrl(objectPath);
  const { error } = await supabase.from('media_assets').insert({ object_path: objectPath, public_url: publicUrl.publicUrl, mime_type: file.type, byte_size: file.size, checksum_sha256: digest });
  if (error) {
    // Compensate only this newly uploaded object; never remove another asset.
    let cleanupFailed = false;
    try {
      const cleanup = await supabase.storage.from('article-media').remove([objectPath]);
      cleanupFailed = Boolean(cleanup.error);
    } catch { cleanupFailed = true; }
    return cleanupFailed
      ? errorResponse('Metadati non salvati; il file richiede pulizia amministrativa', 500, 'media_cleanup_required')
      : errorResponse(error.message, 400, 'database_error');
  }
  return response({ image_url: publicUrl.publicUrl }, 201);
}

async function supabaseRequest(path, init = {}) {
  const method = (init.method || 'GET').toUpperCase();
  const url = new URL(path, window.location.origin);
  const body = parseBody(init) || {};
  if (method === 'GET' && /^\/api\/(news|services|results|testimonials)(\/|$)/.test(url.pathname)) return publicRead(path);
  const content = await adminContent(path, init);
  if (content) return content;
  if (url.pathname === '/api/admin/me') {
    return edge('admin-data', { method: 'GET' }, '?resource=me');
  }
  if (url.pathname === '/api/contact') return edge('contact-submit', init);
  if (url.pathname.startsWith('/api/newsletter/')) return edge('newsletter', {
    ...init, body: JSON.stringify({ ...body, action: url.pathname.endsWith('/unsubscribe') ? 'unsubscribe' : 'subscribe' }),
  });
  if (url.pathname === '/api/chat') return edge('chat', init);
  if (url.pathname === '/api/calendar/status') return edge('calendar', { method: 'GET' }, '?action=status');
  if (url.pathname.includes('/calendar/available-slots') || url.pathname.includes('/calendar/suggested-slots')) return edge('calendar', { method: 'GET' });
  if (url.pathname === '/api/calendar/check-slot') return edge('calendar', { ...init, body: JSON.stringify({ ...body, action: 'check' }) });
  if (url.pathname === '/api/calendar/book') return edge('calendar', { ...init, body: JSON.stringify({ ...body, action: 'book' }) });
  if (url.pathname === '/api/oauth/calendar/login') return edge('google-calendar-oauth', { method: 'GET' });
  if (url.pathname === '/api/admin/upload-image-file') return uploadImage(init);
  if (url.pathname === '/api/admin/ai/format-news') return edge('ai-content', { ...init, body: JSON.stringify({ ...body, action: 'format' }) });
  if (url.pathname === '/api/admin/ai/generate-article') {
    const article = await edge('ai-content', { ...init, body: JSON.stringify({ ...body, action: 'generate' }) });
    if (!article.ok || !body.generate_image) return article;
    const generated = await article.clone().json();
    const imageResult = await edge('ai-image', { ...init, body: JSON.stringify({ prompt: `${body.keyword}. Immagine editoriale premium senza testo.` }) });
    if (imageResult.ok) generated.image_url = (await imageResult.json()).image_url;
    return response(generated);
  }
  if (url.pathname === '/api/admin/ai/generate-image') {
    const prompt = body.custom_prompt || `${body.title || ''}. Categoria ${body.category || 'Business'}. Immagine editoriale premium senza testo.`;
    return edge('ai-image', { ...init, body: JSON.stringify({ prompt }) });
  }

  let query = '';
  let edgeName = '';
  if (url.pathname === '/api/admin/retention-references') {
    if ((init.method || 'GET').toUpperCase() !== 'GET') return errorResponse('Operazione di sola lettura', 405, 'method_not_allowed');
    const params = new URLSearchParams({ resource: 'retention-references' });
    for (const key of ['kind', 'before', 'after']) {
      if (url.searchParams.has(key)) params.set(key, url.searchParams.get(key));
    }
    return edge('admin-data', { ...init, cache: 'no-store' }, `?${params}`);
  }
  if (url.pathname === '/api/admin/conversations') { edgeName = 'admin-data'; query = '?resource=conversations'; }
  else if (/^\/api\/admin\/conversations\/[^/]+$/.test(url.pathname)) {
    edgeName = 'admin-data';
    query = `?resource=${(init.method || 'GET').toUpperCase() === 'DELETE' ? 'conversation' : 'conversations'}&id=${encodeURIComponent(url.pathname.split('/').pop())}`;
  }
  else if (url.pathname === '/api/admin/contact-requests') { edgeName = 'admin-data'; query = `?resource=contacts${url.searchParams.get('status') ? `&status=${encodeURIComponent(url.searchParams.get('status'))}` : ''}`; }
  else if (url.pathname === '/api/admin/contact-requests/stats') { edgeName = 'admin-data'; query = '?resource=contact-stats'; }
  else if (/^\/api\/admin\/contact-requests\/[^/]+\/status$/.test(url.pathname)) { edgeName = 'admin-data'; query = `?resource=contact&id=${encodeURIComponent(url.pathname.split('/').at(-2))}`; init = { ...init, method: 'PATCH', body: JSON.stringify({ status: url.searchParams.get('status') }) }; }
  else if (/^\/api\/admin\/contact-requests\/[^/]+$/.test(url.pathname)) { edgeName = 'admin-data'; query = `?resource=contact&id=${encodeURIComponent(url.pathname.split('/').pop())}`; }
  else if (url.pathname === '/api/admin/dashboard/stats') { edgeName = 'admin-data'; query = '?resource=dashboard'; }
  else if (url.pathname === '/api/admin/dashboard/chart-data') { edgeName = 'admin-data'; query = `?resource=chart&days=${encodeURIComponent(url.searchParams.get('days') || '14')}`; }
  else if (url.pathname === '/api/admin/export-articles') { edgeName = 'admin-data'; query = '?resource=export-articles'; }
  else if (url.pathname === '/api/admin/import-articles') { edgeName = 'admin-data'; query = '?resource=import-articles'; }
  else if (url.pathname === '/api/admin/newsletter/subscribers') { edgeName = 'newsletter-admin'; query = '?resource=subscribers'; }
  else if (url.pathname === '/api/admin/newsletter/stats') { edgeName = 'newsletter-admin'; query = '?resource=stats'; }
  else if (url.pathname === '/api/admin/newsletter/history') { edgeName = 'newsletter-admin'; query = '?resource=history'; }
  else if (url.pathname === '/api/admin/newsletter/send') { edgeName = 'newsletter-admin'; }
  else if (url.pathname === '/api/admin/newsletter/preview') { edgeName = 'newsletter-admin'; query = '?resource=preview'; }
  else if (/^\/api\/admin\/newsletter\/subscriber\/[^/]+$/.test(url.pathname)) { edgeName = 'newsletter-admin'; query = `?subscriber_id=${encodeURIComponent(url.pathname.split('/').pop())}`; }
  if (edgeName) return edge(edgeName, init, query);
  if (url.pathname === '/api/admin/import-data' || url.pathname === '/api/admin/news/cleanup-demo') return errorResponse('Endpoint legacy dismesso', 410, 'legacy_endpoint_gone');
  return errorResponse('Endpoint API non disponibile', 410, 'legacy_endpoint_gone');
}

function withIdempotency(path, init = {}) {
  if (!['/api/contact', '/api/calendar/book'].includes(new URL(path, window.location.origin).pathname)) return init;
  const headers = new Headers(init.headers);
  if (!headers.has('Idempotency-Key')) headers.set('Idempotency-Key', crypto.randomUUID());
  return { ...init, headers };
}

async function compareShadow(path, init, legacyResponse) {
  if ((init.method || 'GET').toUpperCase() !== 'GET' || !supabaseConfigured || !/^\/api\/(news|services|results|testimonials)/.test(new URL(path, window.location.origin).pathname)) return;
  try {
    const [legacyData, shadowResponse] = await Promise.all([legacyResponse.clone().json(), supabaseRequest(path, init)]);
    const shadowData = await shadowResponse.json();
    const legacyCount = Array.isArray(legacyData) ? legacyData.length : Number(Boolean(legacyData));
    const shadowCount = Array.isArray(shadowData) ? shadowData.length : Number(Boolean(shadowData));
    if (!shadowResponse.ok || legacyCount !== shadowCount) console.warn('Shadow read mismatch', { path: new URL(path, window.location.origin).pathname, legacyCount, shadowCount });
  } catch (error) {
    console.warn('Shadow read unavailable', { path: new URL(path, window.location.origin).pathname, message: error.message });
  }
}

export async function apiFetch(input, init = {}) {
  const path = apiPath(input);
  const prepared = withIdempotency(path, init);
  if (dataBackend === 'supabase') {
    if (!supabaseConfigured) return errorResponse('Configurazione Supabase mancante', 503, 'misconfigured');
    return supabaseRequest(path, prepared);
  }
  const target = typeof input === 'string' ? input : input.url;
  const legacy = await window.fetch(target || `${backendUrl}${path}`, prepared);
  if (dataBackend === 'shadow') void compareShadow(path, prepared, legacy);
  return legacy;
}

export function absoluteMediaUrl(value) {
  if (!value || /^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${backendUrl}${value}`;
}
